import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Lock, Star as StarIcon, Sparkles, Loader2, MapPin, MessageCircle, Copy, Check as CheckIcon, Send, Coins } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import VerifiedBadge from "@/components/VerifiedBadge";
import MatchRevealModal from "@/components/MatchRevealModal";
import { useAuth } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";

export default function Matches() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ matches: [], subscribed: false });
  const [loading, setLoading] = useState(true);
  const [busyMatchId, setBusyMatchId] = useState(null);
  const [revealMatch, setRevealMatch] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches");
      setData(data);
    } catch {
      toast.error("Could not load matches");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // If we arrived here from a successful reveal purchase (Stripe), auto-open the reveal modal
  useEffect(() => {
    const revealId = searchParams.get("reveal");
    if (!revealId || loading) return;
    const m = data.matches.find((x) => x.match_id === revealId && x.unlocked);
    if (m) {
      setRevealMatch(m);
      const next = new URLSearchParams(searchParams);
      next.delete("reveal");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, data.matches, loading, setSearchParams]);

  const credits = user?.reveal_credits || 0;

  const unlockWithCredit = async (matchId) => {
    setBusyMatchId(matchId);
    try {
      await api.post(`/matches/${matchId}/spend-credit`);
      await refresh();
      const { data: fresh } = await api.get("/matches");
      setData(fresh);
      const unlocked = fresh.matches.find((x) => x.match_id === matchId && x.unlocked);
      if (unlocked) setRevealMatch(unlocked);
    } catch (err) {
      toast.error(errorMessage(err, "Could not unlock"));
    } finally {
      setBusyMatchId(null);
    }
  };

  const unlockOne = async (matchId, matchUserId) => {
    setBusyMatchId(matchId);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "match_reveal",
        origin_url: window.location.origin,
        match_user_id: matchUserId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Could not start checkout"));
      setBusyMatchId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Your matches</span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-2">{data.matches.length === 1 ? "1 person liked you back." : `${data.matches.length} people liked you back.`}</h1>
          <p className="mt-3 text-lg text-[#5c5c5c]">Unlock a single reveal — or go unlimited with a membership.</p>
        </div>
        {data.subscribed ? (
          <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium" data-testid="matches-subscribed-badge">
            <Sparkles className="w-4 h-4 text-[#c39d2b]" /> Membership active
          </div>
        ) : credits > 0 ? (
          <div className="flex items-center gap-3 flex-wrap" data-testid="matches-credits-balance">
            <div className="glass px-5 py-3 rounded-full flex items-center gap-2 text-sm text-[#722f37] font-medium">
              <Coins className="w-4 h-4 text-[#c39d2b]" /> {credits} reveal credit{credits === 1 ? "" : "s"}
            </div>
            <Link to="/pricing" className="btn-outline !py-2 !px-4 text-sm">Get more</Link>
          </div>
        ) : (
          <Link to="/pricing" className="btn-gold" data-testid="matches-upgrade-btn">Buy credits or subscribe</Link>
        )}
      </div>

      {data.matches.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-[#e5e0d8] card-elev text-center" data-testid="matches-empty">
          <h3 className="font-serif-display text-3xl text-[#722f37]">No matches yet.</h3>
          <p className="mt-3 text-[#5c5c5c] text-lg">Keep discovering — matches happen when someone likes you back.</p>
          <div className="mt-6"><Link to="/discover" className="btn-primary" data-testid="matches-go-discover">Discover people</Link></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.matches.map((m) => (
            <MatchCard
              key={m.match_id}
              match={m}
              credits={credits}
              onUnlockWithCredit={() => unlockWithCredit(m.match_id)}
              onUnlockPaid={() => unlockOne(m.match_id, m.user.id)}
              busy={busyMatchId === m.match_id}
            />
          ))}
        </div>
      )}

      {revealMatch && (
        <MatchRevealModal match={revealMatch} onClose={() => setRevealMatch(null)} />
      )}
    </div>
  );
}

function MatchCard({ match, credits, onUnlockWithCredit, onUnlockPaid, busy }) {
  const u = match.user;
  const photo = u.photos?.[0];
  const [openers, setOpeners] = useState(null);
  const [loadingOpeners, setLoadingOpeners] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const loadIcebreakers = async () => {
    if (openers || loadingOpeners) return;
    setLoadingOpeners(true);
    try {
      const { data } = await api.post(`/matches/${match.match_id}/icebreakers`);
      setOpeners(data.openers || []);
    } catch (err) {
      toast.error(errorMessage(err, "Could not generate icebreakers"));
    } finally {
      setLoadingOpeners(false);
    }
  };

  const copyOpener = (text, i) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(i);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedIdx(null), 1600);
    }).catch(() => toast.error("Copy failed"));
  };

  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-[#e5e0d8] card-elev flex flex-col" data-testid={`match-card-${match.match_id}`}>
      <div className="relative aspect-[4/5] bg-[#eae5d9]">
        {match.unlocked && photo?.data_url ? (
          <img src={photo.data_url} alt={u.display_name} className="w-full h-full object-cover" />
        ) : (
          <>
            <div className="w-full h-full locked-blur" style={{
              background: "linear-gradient(135deg,#c9b8a0,#a08a70)",
            }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <div className="glass rounded-2xl px-6 py-6 max-w-[85%]">
                <Lock className="w-6 h-6 text-[#722f37] mx-auto" />
                <div className="font-serif-display text-2xl text-[#722f37] mt-3">Someone liked you back</div>
                <p className="text-sm text-[#5c5c5c] mt-2">Unlock to see photos & name.</p>
                {credits > 0 ? (
                  <>
                    <button
                      onClick={onUnlockWithCredit}
                      disabled={busy}
                      className="btn-primary mt-5 w-full flex items-center justify-center gap-2"
                      data-testid={`match-credit-unlock-btn-${match.match_id}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                      Use 1 credit ({credits} left)
                    </button>
                    <div className="text-[11px] text-[#5c5c5c] mt-2">
                      Or <button onClick={onUnlockPaid} className="underline hover:text-[#722f37]" data-testid={`match-unlock-btn-${match.match_id}`}>pay $10 once</button>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onUnlockPaid}
                      disabled={busy}
                      className="btn-gold mt-5 w-full flex items-center justify-center gap-2"
                      data-testid={`match-unlock-btn-${match.match_id}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                      Unlock for $10
                    </button>
                    <div className="text-[11px] text-[#5c5c5c] mt-2">
                      <Link to="/pricing" className="underline hover:text-[#722f37]">Save with a 3 or 10-pack</Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {match.unlocked ? (
          <>
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif-display text-2xl text-[#722f37]" data-testid={`match-name-${match.match_id}`}>
                  {u.display_name}, <span className="text-[#5c5c5c]">{u.age}</span>
                </h3>
                {u.verified && <VerifiedBadge size={12} />}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <StarIcon className="w-4 h-4 text-[#c39d2b] fill-[#c39d2b]" />
                <span className="font-medium">{u.overall_rating || "—"}</span>
              </div>
            </div>
            {u.location && (
              <div className="text-sm text-[#5c5c5c] mt-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {u.location}
              </div>
            )}
            {u.bio && <p className="text-sm text-[#5c5c5c] mt-3 line-clamp-3">{u.bio}</p>}

            <div className="mt-5 pt-5 border-t border-[#e5e0d8]">
              <Link
                to={`/chat/${match.match_id}`}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-[#722f37] text-white py-2.5 text-sm font-medium hover:bg-[#8c3a45] transition-colors mb-3"
                data-testid={`match-message-btn-${match.match_id}`}
              >
                <Send className="w-4 h-4" /> Message {u.display_name}
              </Link>
              {!openers && (
                <button
                  onClick={loadIcebreakers}
                  disabled={loadingOpeners}
                  className="w-full flex items-center justify-center gap-2 rounded-full border border-[#722f37] text-[#722f37] py-2.5 text-sm font-medium hover:bg-[#722f37] hover:text-white transition-colors"
                  data-testid={`icebreakers-btn-${match.match_id}`}
                >
                  {loadingOpeners ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                  {loadingOpeners ? "Writing openers…" : "Break the ice"}
                </button>
              )}
              {openers && openers.length > 0 && (
                <div className="space-y-2" data-testid={`icebreakers-list-${match.match_id}`}>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#722f37] mb-2">Suggested openers</div>
                  {openers.map((o, i) => (
                    <div key={i} className="rounded-xl bg-[#faf9f6] border border-[#e5e0d8] p-3 text-sm text-[#1a1a1a]">
                      <p className="leading-relaxed">{o}</p>
                      <button
                        onClick={() => copyOpener(o, i)}
                        className="mt-2 text-xs text-[#722f37] font-medium flex items-center gap-1 hover:underline"
                        data-testid={`icebreaker-copy-${match.match_id}-${i}`}
                      >
                        {copiedIdx === i ? <><CheckIcon className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-[#5c5c5c]">Age {u.age}{u.location ? ` · ${u.location}` : ""}</div>
            <div className="text-xs text-[#5c5c5c] mt-1">Rating: {u.overall_rating || "—"}</div>
          </>
        )}
      </div>
    </div>
  );
}
